import { throwError as observableThrowError, Observable } from "rxjs";
import { SharedMethodsModule } from "../insa-custom-scripts/sharedMethods";

import { refCount, publishReplay, catchError } from "rxjs/operators";
import { Injectable, OnDestroy, Output, EventEmitter } from "@angular/core";
import { AuthService } from "./auth.service";
import { appConfig } from "../../insaweb.config";
//import { Http } from '@angular/http';
import { HttpClient } from "@angular/common/http";

@Injectable()
export class LocalizeService implements OnDestroy {
  private hostAddress = appConfig.insaServiceAddress;
  private servicesURL = "/InsaService";

  // use insaweb.config key to make available locales
  private availableLocales: Array<string> = appConfig.availableLocales || [
    "en-US",
  ];

  private _authSubscription = undefined;
  private skipTags = ["svg", "table"];
  private selectedLocale: string =
    (appConfig as any).defaultLocale || this.availableLocales[0] || "en-US";
  private defaultLocale: string = this.selectedLocale;
  private navigatorLocale: string =
    navigator.language || navigator["browserLanguage"]; // with fallback for < ie11

  private localeResources = undefined;
  // translations for login page (LOOKUP method)
  private translations = require("./loginTranslations").default;

  @Output() LocaleChangedEmmiter = new EventEmitter();
  constructor(
    private _authService: AuthService,
    private http: HttpClient,
  ) {
    // load resources after login
    this._authSubscription =
      this._authService.LoggedInStatusChangedEmitter.subscribe((loggedIn) => {
        if (loggedIn.value) {
          this.loadResources(this.selectedLocale);
        }
      });
    // load previously selected locale
    if (localStorage["selected-locale"] !== undefined) {
      this.setLocale(localStorage["selected-locale"]);
    } else {
      // prefer the config-defined default locale, then fall back to navigator language
      const configDefault: string = (appConfig as any).defaultLocale;
      if (configDefault && this.availableLocales.indexOf(configDefault) > -1) {
        this.setLocale(configDefault);
      } else if (
        this.availableLocales.findIndex((l) => l === this.navigatorLocale) > -1
      ) {
        this.setLocale(this.navigatorLocale);
      }
    }
  }

  ngOnDestroy() {
    if (this._authSubscription) {
      this._authSubscription.unsubscribe();
    }
  }

  setLocale(locale: string) {
    if (window["pdfjsLib"] && window["pdfjsLib"].PDFJS) {
      window["pdfjsLib"].PDFJS.locale = locale;
    }
    if (this.selectedLocale !== locale) {
      this.selectedLocale = locale;
      localStorage["selected-locale"] = locale;
      return this.loadResources(locale).then(() => {
        this.LocaleChangedEmmiter.emit();
      });
    } else {
      return Promise.resolve();
    }
  }

  getLocale() {
    return this.selectedLocale;
  }

  getTranslation(text: string, toLocale: string = this.selectedLocale) {
    // console.log(text)
    if (text === null || text === undefined) {
      return text;
    }
    let trans =
      this.translations[toLocale] !== undefined
        ? this.translations[toLocale][text.trim()]
        : text;
    if (trans != "" && trans !== null && trans !== undefined) {
      return trans;
    } else {
      return text;
    }
  }

  getLocalizedResource(res: string) {
    // DN: when the resources are being downloaded for the first time localResources variable is undefined, but another translation is started in parallel
    // additional check to prevent exception
    let lok = this.localeResources ? this.localeResources[res] : undefined;
    // if (lok === undefined || lok === null || lok.trim() === '') {
    //   console.log('No translation found for resource: ' + res);
    // }
    return lok;
  }

  getAvailableLocales() {
    return this.availableLocales;
  }

  localeNameMapping(locale) {
    const mapping = {
      "en-US": ["English", "Res594"],
      "de-DE": ["German", "Res593"],
      "es-ES": ["Spanish", "Res3356"],
      "it-IT": ["Italian", "Res596"],
      "hr-HR": ["Croatian", "Res3354"],
      "fr-FR": ["French", "Res595"],
      "sr-RS": ["Serbian", "Res453"],
    };

    if (this.localeResources) {
      return (
        this.getLocalizedResource(mapping[locale][1]) || mapping[locale][0]
      );
    }
    return mapping[locale][0];
  }

  getAvailableLocalesWithFriendlyNames() {
    return this.availableLocales.map((loc) => {
      return {
        locale: loc,
        description: this.localeNameMapping(loc),
      };
    });
  }

  performTextChangeFromTranslations(element: Element = undefined) {
    if (!element) {
      element = window.document.body;
    }
    // allow skipping localization (e.g. grids and stuff)
    if (
      element.hasAttribute("prevent-localize") ||
      this.skipTags.find((tag) => tag === element.tagName)
    ) {
      return;
    }
    // if there are no child nodes - try to change text
    // if not found in dictionary keep the existing one
    if (
      element.children === undefined ||
      (element.children !== undefined && element.children.length === 0)
    ) {
      if (this.selectedLocale === this.defaultLocale) {
        if (element.hasAttribute("text-" + this.defaultLocale)) {
          element.innerHTML = element.getAttribute(
            "text-" + this.defaultLocale,
          );
        }
        // placeholder translation for inputs
        if (
          element.getAttribute("placeholder") &&
          element.hasAttribute("placeholdertext-" + this.defaultLocale)
        ) {
          element.setAttribute(
            "placeholder",
            element.getAttribute("placeholdertext-" + this.defaultLocale),
          );
        }
      } else {
        let innerHTML = element.innerHTML;
        let trans;
        if (element.hasAttribute("text-" + this.defaultLocale)) {
          trans = this.getTranslation(
            element.getAttribute("text-" + this.defaultLocale),
          );
        } else {
          trans = this.getTranslation(innerHTML);
        }
        if (trans !== undefined) {
          // save original text
          if (
            innerHTML &&
            innerHTML !== "" &&
            !element.hasAttribute("text-" + this.defaultLocale) &&
            this.selectedLocale !== this.defaultLocale
          ) {
            element.setAttribute("text-" + this.defaultLocale, innerHTML);
          }
          element.innerHTML = trans;
        }
        // placeholder translation for inputs
        trans = undefined;
        if (element.hasAttribute("placeholdertext-" + this.defaultLocale)) {
          trans = this.getTranslation(
            element.getAttribute("placeholdertext-" + this.defaultLocale),
          );
        } else {
          trans = this.getTranslation(element.getAttribute("placeholder"));
        }
        if (trans !== undefined) {
          // save original text
          if (
            element.getAttribute("placeholder") &&
            !element.hasAttribute("placeholdertext-" + this.defaultLocale) &&
            this.selectedLocale !== this.defaultLocale
          ) {
            element.setAttribute(
              "placeholdertext-" + this.defaultLocale,
              element.getAttribute("placeholder"),
            );
          }
          element.setAttribute("placeholder", trans);
        }
      }
    } else {
      if (this.selectedLocale === this.defaultLocale) {
        // try to translate first child if it's text
        if (
          element.firstChild &&
          element.firstChild.nodeType === Node.TEXT_NODE &&
          element.hasAttribute("text-" + this.defaultLocale)
        ) {
          element.firstChild.textContent = element.getAttribute(
            "text-" + this.defaultLocale,
          );
        }
      } else {
        // try to translate first child if it's text
        if (
          element.firstChild &&
          element.firstChild.nodeType === Node.TEXT_NODE
        ) {
          let innerText = element.firstChild.textContent;
          let trans;
          if (element.hasAttribute("text-" + this.defaultLocale)) {
            trans = this.getTranslation(
              element.getAttribute("text-" + this.defaultLocale),
            );
          } else {
            trans = this.getTranslation(innerText);
          }
          if (trans !== undefined) {
            // save original text
            if (
              innerText &&
              innerText !== "" &&
              !element.hasAttribute("text-" + this.defaultLocale) &&
              this.selectedLocale !== this.defaultLocale
            ) {
              element.setAttribute("text-" + this.defaultLocale, innerText);
            }
            element.firstChild.textContent = trans;
          }
        }
      }
      if (element.children) {
        for (let i = 0; i < element.children.length; i++) {
          this.performTextChangeFromTranslations(element.children[i]);
        }
      }
    }
  }

  /* >>>> BEGIN copy from insaService */

  /* extracted from insaService
    /  angular didn't like circular service dependency
    */
  private handleError(error: any) {
    let errMsg = error.message
      ? error.message
      : error.status
        ? `${error.status} - ${error.statusText}`
        : "Server error";
    return observableThrowError(errMsg);
  }

  getTranslations(languageCode) {
    let headers = this._authService.getAuthHeaders("application/json");
    return (
      this.http
        .get(
          `${this.hostAddress}${this.servicesURL}/Translations?Language=${languageCode}&SessionID='${this._authService.sessionId}`,
          { headers },
        )
        .pipe(
          publishReplay(),
          refCount(),
          catchError((err) => this.handleError(err)),
        )
        //.map(res => res.json())
        .toPromise()
        .catch((e) => {
          console.log(e);
        })
    );
  }

  /* <<< END copy from insaService */

  loadResources(locale: string) {
    // console.log('loadResources: ' + locale);

    // migrate stale 2-letter "sr" cache key → full "sr-RS" key
    if (localStorage["translations-sr"] !== undefined) {
      localStorage.removeItem("translations-sr");
      localStorage.removeItem("translations-version-sr");
    }

    // get resources from WebAPI - only if logged in
    let lang = locale; //locale.substring(0, 2);
    if (locale === "de-DE") {
      lang = locale.substring(0, 2);
    }
    const kendoLocaleMap = {
      "sr-RS": "sr",
    };

    const kendoLocale = kendoLocaleMap[locale] || locale;
    // require Kendo stuff /Kendo/2020.2.617/locales/en-EN.min.js
    require.ensure([], function () {
      require(
        "../../Kendo/2020.2.617/js/cultures/kendo.culture." +
          kendoLocale +
          ".min.js",
      );
      try {
        delete require.cache[
          require.resolve(
            "../../Kendo/2020.2.617/js/messages/kendo.messages." +
              locale +
              ".min.js",
          )
        ];
        require(
          "../../Kendo/2020.2.617/js/messages/kendo.messages." +
            locale +
            ".min.js",
        );
      } catch (e) {
        // console.log('File not found: kendo.messages.' + locale + '.min.js')
      }
    });
    // check if translations need to be reloaded - e.g. after updating resources
    if (
      localStorage["translations-version-" + lang] === undefined ||
      parseFloat(localStorage["translations-version-" + lang]) <
        appConfig.translationsVersion
    ) {
      localStorage.setItem("translations-force-reload", "YES");
    }
    if (
      this._authService.isLoggedIn() &&
      (localStorage["translations-" + lang] === undefined ||
        localStorage["translations-force-reload"] === "YES")
    ) {
      return this.getTranslations(lang).then((data: string) => {
        // holi data:string
        // don't save bad translations
        if (data !== undefined && data !== null) {
          localStorage["translations-" + lang] = data;
          this.localeResources = JSON.parse(data);
          SharedMethodsModule.setNoDataText(this.getLocalizedResource('Res1133'));
          localStorage.removeItem("translations-force-reload");
          localStorage.setItem(
            "translations-version-" + lang,
            appConfig.translationsVersion.toString(),
          );
        }
        return;
      });
    } else {
      let ls = localStorage["translations-" + lang];
      if (ls !== undefined) {
        this.localeResources = JSON.parse(ls);
        SharedMethodsModule.setNoDataText(this.getLocalizedResource('Res1133'));
      } else {
        this.localeResources = undefined;
      }
      // require Kendo stuff /Kendo/2020.2.617/locales/en-EN.min.js
      require.ensure([], function () {
        require(
          "../../Kendo/2020.2.617/js/cultures/kendo.culture." +
            kendoLocale +
            ".min.js",
        );

        try {
          require(
            "../../Kendo/2020.2.617/js/messages/kendo.messages." +
              locale +
              ".min.js",
          );
        } catch (e) {
          // console.log('File not found: kendo.messages.' + locale + '.min.js')
        }
      });
      return Promise.resolve();
    }
  }

  performTextChangeFromResources(element: Element = undefined) {
    if (!element) {
      element = window.document.body;
    }
    // allow skipping localization (e.g. grids and stuff, also good for performance)
    if (element.hasAttribute("prevent-localize")) {
      return;
    }
    // if there are no child nodes - try to change text
    // if not found in dictionary keep the existing one
    if (
      element.children === undefined ||
      (element.children !== undefined && element.children.length === 0)
    ) {
      if (element.hasAttribute("localize")) {
        let trans = this.getLocalizedResource(element.getAttribute("localize"));
        if (trans !== undefined) {
          element.innerHTML = trans;
        }
      }
    } else {
      // try to translate first child if it's text
      if (
        element.firstChild &&
        element.firstChild.nodeType === Node.TEXT_NODE &&
        element.hasAttribute("localize")
      ) {
        let trans = this.getLocalizedResource(element.getAttribute("localize"));
        if (trans !== undefined) {
          element.firstChild.textContent = trans;
        }
      }
      if (element.children) {
        for (let i = 0; i < element.children.length; i++) {
          this.performTextChangeFromResources(element.children[i]);
        }
      }
    }
  }

  applyTranslations(
    fromElement: Element = undefined,
    method: string = "RESOURCES",
  ) {
    // console.log('applyTranslations')
    // console.log(fromElement);
    // console.log(method)
    // method can be LOOKUP or RESOURCES
    // LOOKUP will check in local storage if translations are available
    if (method === "LOOKUP") {
      this.performTextChangeFromTranslations(fromElement);
      return;
    }
    this.loadResources(this.selectedLocale).then(() => {
      this.performTextChangeFromResources(fromElement);
    });
  }
}



// WEBPACK FOOTER //
// ./app/insa-services/localize.service.ts